<script setup lang="ts">
import { computed } from 'vue';
import { withBase } from 'ufo';

const props = defineProps<{
  tabletSrc: string;
  mobileSrc: string;
}>();

/**
 * Fixes for:
 * - baseURL deployments (/app/ etc.)
 * - SVG <image> loading quirks (Safari/Chrome)
 * - empty rendering due to missing xlink namespace
 */
const { app } = useRuntimeConfig();

const resolvedTabletSrc = computed(() =>
  withBase(props.tabletSrc, (app.baseURL as string) || '/')
);
const resolvedMobileSrc = computed(() =>
  withBase(props.mobileSrc, (app.baseURL as string) || '/')
);
</script>

<template>
  <!-- Shadow/Glow NICHT im SVG, sondern hier (GPU-sicher) -->
  <div
    class="relative rounded-3xl bg-black/20 p-2 shadow-2xl shadow-black/60 ring-1 ring-white/10"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 1200 760"
      role="img"
      :aria-label="$t('homePage.hero.previewCaption')"
      class="w-full h-auto"
    >
      <defs>
        <linearGradient id="frame" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stop-color="#222832" />
          <stop offset="1" stop-color="#0a0f18" />
        </linearGradient>

        <linearGradient id="bezel" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stop-color="#0b1220" />
          <stop offset="1" stop-color="#050912" />
        </linearGradient>

        <linearGradient id="glass" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.14" />
          <stop offset="0.45" stop-color="#ffffff" stop-opacity="0.05" />
          <stop offset="1" stop-color="#ffffff" stop-opacity="0.00" />
        </linearGradient>

        <clipPath id="clipTabletScreen">
          <rect x="456" y="162" width="608" height="406" rx="26" ry="26" />
        </clipPath>

        <clipPath id="clipPhoneScreen">
          <rect x="272" y="242" width="216" height="406" rx="34" ry="34" />
        </clipPath>

        <clipPath id="clipPhoneNotch">
          <rect x="344" y="255" width="72" height="16" rx="8" />
        </clipPath>
      </defs>

      <!-- “Fake shadow” als einfache Shape (stabil, kein Filter) -->
      <ellipse cx="760" cy="615" rx="360" ry="34" fill="#000" opacity="0.35" />
      <ellipse cx="380" cy="705" rx="170" ry="26" fill="#000" opacity="0.35" />

      <!-- TABLET -->
      <g>
        <rect
          x="420"
          y="130"
          width="680"
          height="470"
          rx="44"
          fill="url(#frame)"
        />
        <rect
          x="442"
          y="152"
          width="636"
          height="426"
          rx="34"
          fill="url(#bezel)"
          opacity="0.95"
        />

        <g clip-path="url(#clipTabletScreen)">
          <rect x="456" y="162" width="608" height="406" fill="#0b1220" />

          <!-- FIX: use resolved path + include xlink + keep same-origin if possible -->
          <image
            :href="resolvedTabletSrc"
            :xlink:href="resolvedTabletSrc"
            x="456"
            y="162"
            width="608"
            height="406"
            preserveAspectRatio="xMidYMid slice"
          />

          <rect x="456" y="162" width="608" height="406" fill="url(#glass)" />
        </g>

        <circle cx="760" cy="148" r="4" fill="#111827" opacity="0.8" />
      </g>

      <!-- PHONE -->
      <g>
        <rect
          x="240"
          y="210"
          width="280"
          height="480"
          rx="54"
          fill="url(#frame)"
        />
        <rect
          x="258"
          y="228"
          width="244"
          height="444"
          rx="44"
          fill="url(#bezel)"
          opacity="0.96"
        />

        <g clip-path="url(#clipPhoneScreen)">
          <rect x="272" y="242" width="216" height="406" fill="#0b1220" />

          <image
            :href="resolvedMobileSrc"
            :xlink:href="resolvedMobileSrc"
            x="272"
            y="242"
            width="216"
            height="406"
            preserveAspectRatio="xMidYMid slice"
          />

          <rect x="272" y="242" width="216" height="406" fill="url(#glass)" />
        </g>

        <g clip-path="url(#clipPhoneNotch)">
          <rect
            x="344"
            y="255"
            width="72"
            height="16"
            fill="#050913"
            opacity="0.92"
          />
        </g>
        <rect
          x="347"
          y="258"
          width="48"
          height="6"
          rx="3"
          fill="#111827"
          opacity="0.9"
        />
        <circle cx="408" cy="261" r="3" fill="#111827" opacity="0.9" />
      </g>
    </svg>
  </div>
</template>
